import React from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    InputAdornment,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useNewEmployeeForm, type NewEmployeeFormComponentProps} from '../../hooks/useEmployeeHandlers';
import {useAppNavigation} from '../../context/NavigationContext';

// Must match the 'Human Resources/Branches' key in src/config/componentMap.ts
const BRANCHES_PAGE_SEGMENT = 'Human Resources/Branches';

const NewEmployeeFormComponent: React.FC<NewEmployeeFormComponentProps> = ({
                                                                               newEmployee,
                                                                               handleFormChange,
                                                                               handleCheckboxChange,
                                                                               showAllErrors = false,
                                                                               showBranch = true,
                                                                               backendError,
                                                                           }) => {
    const {t} = useTranslation();

    const {
        approvedBranches,
        branchesLoading,
        handleBlur,
        isTouched,
        errors,
        banks,
        bankAccount,
        handleBankAccountChange,
        handleBankAccountBlur,
        isBankAccountTouched,
    } = useNewEmployeeForm(newEmployee, showAllErrors, handleFormChange);

    const {navigate} = useAppNavigation();

    const handleAddBranchClick = (event: React.MouseEvent) => {
        event.preventDefault();
        navigate(BRANCHES_PAGE_SEGMENT);
    };

    return (
        <>
            {backendError && (
                <Alert severity="error" sx={{mb: 1}}>
                    <Typography variant="body2" fontWeight="bold">{backendError.message}</Typography>
                    {backendError.violations.length > 0 && (
                        <ul style={{margin: '4px 0 0', paddingLeft: '1.25rem'}}>
                            {backendError.violations.map((v, i) => (
                                <li key={i}>{v}</li>
                            ))}
                        </ul>
                    )}
                </Alert>
            )}
            {showBranch && (
                <>
                    <FormControl fullWidth>
                        <InputLabel>{t('common.filters.selectBranch')}</InputLabel>
                        <Select
                            label={t('common.filters.selectBranch')}
                            value={newEmployee.branch}
                            onChange={handleFormChange('branch')}
                            displayEmpty
                            startAdornment={
                                branchesLoading ? (
                                    <InputAdornment position="start">
                                        <CircularProgress size={16}/>
                                    </InputAdornment>
                                ) : null
                            }
                        >
                            {approvedBranches.map((b) => (
                                <MenuItem key={b.id} value={b.id}>
                                    {b.name}
                                </MenuItem>
                            ))}
                        </Select>

                    </FormControl>
                    <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={handleAddBranchClick}
                        underline="hover"
                        sx={{alignSelf: 'flex-start'}}
                    >
                        {t('employee.form.addBranchLink')}
                    </Link>
                </>
            )}
            <Typography variant="subtitle2" color="primary" sx={{mb: 0}}>{t('employee.form.section.personalInfo')}</Typography>
            <TextField
                label={t('employee.form.field.firstName')}
                value={newEmployee.firstName}
                onChange={handleFormChange('firstName')}
                onBlur={handleBlur('firstName')}
                fullWidth
                required
                error={isTouched('firstName') && !!errors.firstName}
                helperText={isTouched('firstName') && errors.firstName}
            />
            <TextField
                label={t('employee.form.field.lastName')}
                value={newEmployee.lastName}
                onChange={handleFormChange('lastName')}
                onBlur={handleBlur('lastName')}
                fullWidth
                required
                error={isTouched('lastName') && !!errors.lastName}
                helperText={isTouched('lastName') && errors.lastName}
            />
            <TextField
                label={t('employee.form.field.personalNo')}
                value={newEmployee.personalNo}
                onChange={handleFormChange('personalNo')}
                onBlur={handleBlur('personalNo')}
                fullWidth
                required
                error={isTouched('personalNo') && !!errors.personalNo}
                helperText={isTouched('personalNo') && errors.personalNo}
            />
            <Divider sx={{my: 1}}/>
            <Typography variant="subtitle2" color="primary" sx={{mb: 0}}>{t('employee.form.section.contactDetails')}</Typography>
            <TextField
                label={t('employee.form.field.email')}
                type="email"
                value={newEmployee.email}
                onChange={handleFormChange('email')}
                onBlur={handleBlur('email')}
                fullWidth
                required
                error={isTouched('email') && !!errors.email}
                helperText={isTouched('email') && errors.email}
                placeholder="example@email.com"
            />
            <TextField
                label={t('employee.form.field.phone')}
                value={newEmployee.phone}
                onChange={handleFormChange('phone')}
                onBlur={handleBlur('phone')}
                fullWidth
                required
                error={isTouched('phone') && !!errors.phone}
                helperText={(isTouched('phone') && errors.phone)}
                placeholder="4XXXXXXXX"
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">(+383)(0)</InputAdornment>
                    ),
                }}
            />
            <Divider sx={{my: 1}}/>
            <Typography variant="subtitle2" color="primary" sx={{mb: 0}}>{t('employee.form.section.employmentDetails')}</Typography>
            <FormControl fullWidth required error={isTouched('employmentType') && !!errors.employmentType}>
                <InputLabel>{t('employee.form.field.employmentType')}</InputLabel>
                <Select
                    value={newEmployee.employmentType}
                    onChange={handleFormChange('employmentType')}
                    onBlur={handleBlur('employmentType')}
                    label={t('employee.form.field.employmentType')}
                >
                    <MenuItem value="Full Time">{t('employee.form.employmentType.fullTime')}</MenuItem>
                    <MenuItem value="Part Time">{t('employee.form.employmentType.partTime')}</MenuItem>
                    <MenuItem value="Contractor">{t('employee.form.employmentType.contractor')}</MenuItem>
                    <MenuItem value="Temporary">{t('employee.form.employmentType.temporary')}</MenuItem>
                </Select>
                {isTouched('employmentType') && errors.employmentType && (
                    <FormHelperText>{errors.employmentType}</FormHelperText>
                )}
            </FormControl>
            {/* Add form (carries bankAccount): taxId is auto-generated by the backend, so no input. */}
            {!newEmployee.bankAccount && (<TextField
                label={t('employee.form.field.taxId')}
                value={newEmployee.taxId ?? ''}
                onChange={handleFormChange('taxId')}
                onBlur={handleBlur('taxId')}
                fullWidth
                error={isTouched('taxId') && !!errors.taxId}
                helperText={isTouched('taxId') && errors.taxId}
            />)}
            {newEmployee.bankAccount && (
                <>
                    <Divider sx={{my: 1}}/>
                    <Typography variant="subtitle2" color="primary" sx={{mb: 0}}>{t('employee.form.section.bankAccount')}</Typography>
                    <TextField
                        select
                        label={t('employee.form.bankAccount.bankName')}
                        value={bankAccount.bankId ?? ''}
                        onChange={(e) => handleBankAccountChange('bankId', e.target.value)}
                        onBlur={handleBankAccountBlur('bankId')}
                        fullWidth
                        required
                        error={isBankAccountTouched('bankId') && !!errors.bankAccount?.bankId}
                        helperText={isBankAccountTouched('bankId') && errors.bankAccount?.bankId}
                    >
                        {banks.map((bank) => (
                            <MenuItem key={bank.id} value={bank.id}>{bank.name}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label={t('employee.form.bankAccount.accountNumber')}
                        value={bankAccount.accountNumber}
                        onChange={(e) => handleBankAccountChange('accountNumber', e.target.value)}
                        onBlur={handleBankAccountBlur('accountNumber')}
                        fullWidth
                        required
                        error={isBankAccountTouched('accountNumber') && !!errors.bankAccount?.accountNumber}
                        helperText={isBankAccountTouched('accountNumber') && errors.bankAccount?.accountNumber}
                    />
                    <TextField
                        label={t('employee.form.bankAccount.iban')}
                        value={bankAccount.iban ?? ''}
                        onChange={(e) => handleBankAccountChange('iban', e.target.value)}
                        fullWidth
                    />
                    <TextField
                        label={t('employee.form.bankAccount.accountHolder')}
                        value={bankAccount.accountHolder ?? ''}
                        onChange={(e) => handleBankAccountChange('accountHolder', e.target.value)}
                        fullWidth
                    />
                </>
            )}
            <Divider sx={{my: 1}}/>
            <Typography variant="subtitle2" color="primary" sx={{mb: 0}}>{t('employee.form.section.statusOptions')}</Typography>
            <FormControl component="fieldset" fullWidth>
                <FormControlLabel
                    label={t('employee.form.activateEmployee')}
                    control={
                        <Switch
                            checked={newEmployee.status === 'Active'}
                            onChange={(e) => {
                                const syntheticEvent = {target: {value: e.target.checked ? 'Active' : 'Inactive'}};
                                handleFormChange('status')(syntheticEvent as any);
                            }}
                            inputProps={{'aria-label': 'Activate Employee'}}
                        />
                    }
                />
            </FormControl>
            <Box sx={{mt: 2, display: 'flex', flexDirection: 'column'}}>
                <FormLabel component="legend">{t('employee.form.section.additionalOptions')}</FormLabel>
                <FormControlLabel
                    control={
                        <Switch
                            checked={newEmployee.isCitizen}
                            onChange={handleCheckboxChange('isCitizen')}
                        />
                    }
                    label={t('employee.form.isCitizen')}
                />
                <FormControlLabel
                    label={t('employee.form.taxExempt')}
                    control={
                        <Switch
                            checked={newEmployee.taxExempt}
                            onChange={handleCheckboxChange('taxExempt')}
                        />
                    }
                />
                <FormControl component="fieldset" fullWidth sx={{mt: 2}} required
                             error={isTouched('residencyStatus') && !!errors.residencyStatus}>
                    <FormLabel component="legend">{t('employee.form.section.residencyStatus')}</FormLabel>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={newEmployee.residencyStatus === 'Permanent Residency'}
                                onChange={(e) => {
                                    const syntheticEvent = {target: {value: e.target.checked ? 'Permanent Residency' : ''}};
                                    handleFormChange('residencyStatus')(syntheticEvent as any);
                                }}
                            />
                        }
                        label={t('employee.form.residency.permanent')}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={newEmployee.residencyStatus === 'Temporary Residency'}
                                onChange={(e) => {
                                    const syntheticEvent = {target: {value: e.target.checked ? 'Temporary Residency' : ''}};
                                    handleFormChange('residencyStatus')(syntheticEvent as any);
                                }}
                            />
                        }
                        label={t('employee.form.residency.temporary')}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={newEmployee.residencyStatus === 'Already Pensioner'}
                                onChange={(e) => {
                                    const syntheticEvent = {target: {value: e.target.checked ? 'Already Pensioner' : ''}};
                                    handleFormChange('residencyStatus')(syntheticEvent as any);
                                }}
                            />
                        }
                        label={t('employee.form.residency.pensioner')}
                    />
                    {isTouched('residencyStatus') && errors.residencyStatus && (
                        <FormHelperText>{errors.residencyStatus}</FormHelperText>
                    )}
                </FormControl>
            </Box>
        </>
    );

};

export default NewEmployeeFormComponent;

